import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaBrain, FaBook, FaBalanceScale, FaSeedling, FaUsers } from 'react-icons/fa';
import styles from './Mission.module.css';

const Mission = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [principle1Ref, principle1InView] = useInView({ threshold: 0.1 });
  const [principle2Ref, principle2InView] = useInView({ threshold: 0.1 });
  const [dialecticsRef, dialecticsInView] = useInView({ threshold: 0.1 });
  const [lawsRef, lawsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Миссия и цели - Кировская пионерская организация';
  }, []);

  return (
      <div className={styles.container}>
        {/* Hero Section */}
        <section
            ref={heroRef}
            className={`${styles.hero} ${heroInView ? styles.animateHero : ''}`}
        >
          <div className={styles.heroContent}>
            <h1 className={styles.title}>
              <FaBrain className={styles.titleIcon} />
              Наша цель - умный ребенок
            </h1>
            <p className={styles.subtitle}>
              Принципы воспитательной программы Кировской пионерской организации
            </p>
          </div>
        </section>

        {/* Основные принципы */}
        <section
            ref={principle1Ref}
            className={`${styles.section} ${principle1InView ? styles.animateSection : ''}`}
        >
          <div className={styles.contentWrapper}>
            <PrincipleCard number="1" title="Ум через преобразование мира" icon={<FaBook />}>
              <p>
                Ум формируется через познание и изменение окружающего мира через решение практических задач.
                Цель познания - формирование точного отражения объективной реальности.
              </p>
              <Quote
                  text="Материалистическая теория, теория отражения предметов мыслью..."
                  author="В.И. Ленин. Материализм и эмпириокритицизм. 1909 г."
              />
            </PrincipleCard>
          </div>
        </section>

        <section
            ref={principle2Ref}
            className={`${styles.section} ${styles.darkSection} ${principle2InView ? styles.animateSection : ''}`}
        >
          <div className={styles.contentWrapper}>
            <PrincipleCard number="2" title="Познание законов развития" icon={<FaBalanceScale />}>
              <p>
                Мир развивается по объективным законам. Познание этих законов - ключ к преобразованию действительности.
                Основной инструмент познания - материалистическая диалектика.
              </p>
              <Quote
                  text="Гегелевскую диалектику... считали величайшим приобретением..."
                  author="В.И. Ленин. Карл Маркс, 1914 г."
              />
            </PrincipleCard>
          </div>
        </section>

        {/* Диалектика */}
        <section
            ref={dialecticsRef}
            className={`${styles.section} ${dialecticsInView ? styles.animateSection : ''}`}
        >
          <div className={styles.contentWrapper}>
            <h2 className={styles.sectionTitle}><FaBalanceScale /> Принципы диалектики</h2>
            <div className={styles.grid}>
              <div className={styles.gridCard}>
                <h3>3.1 Взаимосвязь явлений</h3>
                <p>Все природные и социальные явления существуют во взаимосвязи и взаимной обусловленности</p>
              </div>
              <div className={styles.gridCard}>
                <h3>3.2 Движение и развитие</h3>
                <p>Рассмотрение всех явлений в динамике их развития и преобразования</p>
              </div>
            </div>
          </div>
        </section>

        {/* Законы диалектики */}
        <section
            ref={lawsRef}
            className={`${styles.section} ${styles.darkSection} ${lawsInView ? styles.animateSection : ''}`}
        >
          <div className={styles.contentWrapper}>
            <h2 className={styles.sectionTitle}><FaSeedling /> Законы диалектики</h2>
            <div className={styles.lawsGrid}>
              <LawCard number="4.1" title="Единство противоположностей">
                Борьба и взаимодействие противоположных сил как источник развития
              </LawCard>
              <LawCard number="4.2" title="Переход количества в качество">
                Накопление постепенных изменений приводит к качественным скачкам
              </LawCard>
              <LawCard number="4.3" title="Отрицание отрицания">
                Циклическое развитие через преодоление предыдущих стадий
              </LawCard>
            </div>
          </div>
        </section>

        {/* Заключение */}
        <section className={styles.section}>
          <div className={styles.contentWrapper}>
            <div className={styles.conclusion}>
              <FaUsers className={styles.conclusionIcon} />
              <h2 className={styles.conclusionTitle}>Роль вожатого</h2>
              <p className={styles.conclusionText}>
                Создание условий для самостоятельного мышления пионеров - ключевая задача вожатого
              </p>
            </div>
          </div>
        </section>
      </div>
  );
};

// Компоненты
const PrincipleCard = ({ number, title, children, icon }) => (
    <div className={styles.principleCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardNumber}>{number}</div>
        {icon}
      </div>
      <h3 className={styles.cardTitle}>{title}</h3>
      {children}
    </div>
);

const Quote = ({ text, author }) => (
    <div className={styles.quote}>
      <div className={styles.quoteMark}>“</div>
      <div>
        <p className={styles.quoteText}>{text}</p>
        <cite className={styles.quoteAuthor}>{author}</cite>
      </div>
    </div>
);

const LawCard = ({ number, title, children }) => (
    <div className={styles.lawCard}>
      <div className={styles.lawNumber}>{number}</div>
      <h4 className={styles.lawTitle}>{title}</h4>
      <p className={styles.lawText}>{children}</p>
    </div>
);

export default Mission;